import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FieldProps } from "react-jsonschema-form";
import { FormattedMessage } from "react-intl";
import { Button } from "react-bootstrap";
import { messages } from "../messages";
import { HelpTextMessageNoMargin } from "../../shared/widgets/modificationPriceManagement/styles";
import { ButtonContainer } from "../styles";
import CombinationModal from "./CombinationModal";
import { faUserGroup } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useForm, useWatch } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { useLocaleProvider } from "../../../components/locale-provider";
import { useProperty } from "../../../components/property-provider";
import { getAllConfigurations } from "../../occupation/infrastructure/occupation.api";
import { occupationsQueryKeys } from "../../occupation/infrastructure/cache/OccupationsCacheStrategy";
import { OccupationEntity } from "../../occupation/domain/occupation.entity";

type OccupancyByType = {
  senior: number;
  adults: number;
  juniors: number;
  children: number;
  babies: number;
};

export type FormDataType = {
  occupancyCombinationsList: string[];
  maxOccupancy: number;
  maxOccupancyByType?: OccupancyByType;
};

export type OccupancyGeneratorProps = FormDataType & {
  totalCombinations: number;
};

export type GenerateCombinationsProps = Omit<FormDataType, "occupancyCombinationsList">;

// The max occupancy input shows 1 when it is empty, so 0 or undefined behave as 1.
export const getEffectiveMaxOccupancy = (maxOccupancy?: number) => maxOccupancy || 1;

export function hasValidOccupancyLimits({
  maxOccupancy,
  maxOccupancyByType,
}: GenerateCombinationsProps) {
  if (!maxOccupancyByType) {
    return false;
  }
  const max = getEffectiveMaxOccupancy(maxOccupancy);
  const { adults = 0, juniors = 0, children = 0, babies = 0 } = maxOccupancyByType;
  return adults > 0 && adults <= max && juniors <= max && children <= max && babies <= max;
}

export default function OccupancyGenerator({
  formData,
  onChange,
}: FieldProps<FormDataType | undefined>) {
  const shortLocale = useLocaleProvider().currentLocale.substring(0, 2);
  const property = useProperty();
  const queryClient = useQueryClient();
  const [isOpenCombinationModal, setIsOpenCombinationModal] = useState(false);

  const methods = useForm<OccupancyGeneratorProps>({
    mode: "onChange",
    defaultValues: {
      occupancyCombinationsList: formData?.occupancyCombinationsList || [],
      maxOccupancy: formData?.maxOccupancy,
      maxOccupancyByType: formData?.maxOccupancyByType,
      totalCombinations: 0,
    },
  });

  const occupancyCombinationsList = useWatch({
    control: methods.control,
    name: "occupancyCombinationsList",
  });
  const totalCombinations = useWatch({ control: methods.control, name: "totalCombinations" });
  const maxOccupancy = useWatch({ control: methods.control, name: "maxOccupancy" });
  const maxOccupancyByType = useWatch({ control: methods.control, name: "maxOccupancyByType" });

  const configurationsQueryKey = useMemo(
    () => occupationsQueryKeys.configurations(property.ticker),
    [property.ticker]
  );

  // Closing without saving discards the changes made in the modal.
  const handleCombinationModalHide = useCallback(() => {
    setIsOpenCombinationModal(false);
    methods.reset({
      occupancyCombinationsList: formData?.occupancyCombinationsList || [],
      maxOccupancy: formData?.maxOccupancy,
      maxOccupancyByType: formData?.maxOccupancyByType,
      totalCombinations: methods.getValues("totalCombinations"),
    });
  }, [formData, methods]);

  const openCombinationModal = useCallback(() => {
    // Occupancies may have changed elsewhere, so load the catalogue again once per opening.
    queryClient.invalidateQueries({ queryKey: configurationsQueryKey });
    setIsOpenCombinationModal(true);
  }, [configurationsQueryKey, queryClient]);

  const generateCombinations = useCallback(
    async ({ maxOccupancy, maxOccupancyByType }: GenerateCombinationsProps) => {
      // Cached so recalculating on every change or on save does not refetch the catalogue.
      const occupations = await queryClient.fetchQuery({
        queryKey: configurationsQueryKey,
        queryFn: getAllConfigurations,
        staleTime: 5 * 60 * 1000,
      });
      const max = getEffectiveMaxOccupancy(maxOccupancy);
      const combinations = occupations.filter(({ ticker, adults, juniors, children, babies }) => {
        if (ticker === "pax") return false;
        const total = adults + juniors + children + babies;
        return (
          total <= max &&
          adults <= (maxOccupancyByType?.adults || 0) &&
          juniors <= (maxOccupancyByType?.juniors || 0) &&
          children <= (maxOccupancyByType?.children || 0) &&
          babies <= (maxOccupancyByType?.babies || 0)
        );
      });
      methods.setValue("totalCombinations", combinations.length);
      return combinations;
    },
    [configurationsQueryKey, methods, queryClient]
  );

  // Drops selected combinations that no longer fit the current max occupancy, otherwise they
  // stay hidden in the selection and are still saved.
  const pruneSelectedCombinations = useCallback(
    (combinations: OccupationEntity[]) => {
      const validTickers = new Set(combinations.map(({ ticker }) => ticker));
      const selected = methods.getValues("occupancyCombinationsList") || [];
      const pruned = selected.filter(ticker => validTickers.has(ticker));
      if (pruned.length !== selected.length) {
        methods.setValue("occupancyCombinationsList", pruned);
      }
      return pruned;
    },
    [methods]
  );

  // Resolves to false when nothing was saved because no selected combination fits anymore.
  const handleCombinationModalSave = useCallback(async () => {
    const { maxOccupancy, maxOccupancyByType, occupancyCombinationsList } = methods.getValues();
    let selected = occupancyCombinationsList || [];
    // Without usable per-type limits there is nothing to prune against (e.g. older
    // accommodations), so the selection is saved as is.
    if (hasValidOccupancyLimits({ maxOccupancy, maxOccupancyByType })) {
      const combinations = await generateCombinations({ maxOccupancy, maxOccupancyByType });
      selected = pruneSelectedCombinations(combinations);
      // An empty list is ignored by the backend on update, so keep the modal open instead.
      if (selected.length === 0) return false;
    }
    setIsOpenCombinationModal(false);
    onChange?.({ occupancyCombinationsList: selected, maxOccupancy, maxOccupancyByType });
    return true;
  }, [generateCombinations, methods, onChange, pruneSelectedCombinations]);

  useEffect(() => {
    if (occupancyCombinationsList && occupancyCombinationsList.length > 0) {
      generateCombinations({ maxOccupancy, maxOccupancyByType }).then(() =>
        console.debug("combinations generated")
      );
    }
  }, [generateCombinations, maxOccupancy, maxOccupancyByType, methods, occupancyCombinationsList]);

  return (
    <>
      <ButtonContainer>
        <Button variant="primary" type="button" onClick={openCombinationModal}>
          <FontAwesomeIcon icon={faUserGroup} size={"xs"} className="mr-2" />
          <FormattedMessage {...messages.accommodationOccupancySelection} />
        </Button>
      </ButtonContainer>
      <HelpTextMessageNoMargin>
        <FormattedMessage
          {...messages.accommodationOccupancyGenerated}
          values={{
            max: totalCombinations || 0,
            current: occupancyCombinationsList.length || 0,
          }}
        />
      </HelpTextMessageNoMargin>
      <CombinationModal
        open={isOpenCombinationModal}
        handleHide={handleCombinationModalHide}
        handleSave={handleCombinationModalSave}
        locale={shortLocale}
        methods={methods}
        onGenerateCombinations={generateCombinations}
        onPruneSelectedCombinations={pruneSelectedCombinations}
      />
    </>
  );
}
